import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { db } from '../db';
import { toast } from 'react-hot-toast';
import type { SKU } from '../types';

// 브라우저 환경 확인 함수
const isBrowser = typeof window !== 'undefined';

// 안전한 타이머 함수들
const safeSetTimeout = (callback: Function, delay: number): number | null => {
  return isBrowser ? window.setTimeout(() => callback(), delay) : null;
};

const safeClearTimeout = (timeoutId: number | null): void => {
  if (isBrowser && timeoutId !== null) {
    window.clearTimeout(timeoutId);
  }
};

const safeSetInterval = (callback: Function, delay: number): number | null => {
  return isBrowser ? window.setInterval(() => callback(), delay) : null;
};

const safeClearInterval = (intervalId: number | null): void => {
  if (isBrowser && intervalId !== null) {
    window.clearInterval(intervalId);
  }
};

// 환경 변수 로드 함수 - 에러를 던지지 않고 로깅만 함
const loadEnvVars = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  if (!supabaseUrl || !supabaseKey) {
    console.error('Missing Supabase environment variables');
    if (isBrowser) {
      toast.error('시스템 설정이 잘못되었습니다. 관리자에게 문의하세요.');
    }
    return { supabaseUrl: '', supabaseKey: '', isValid: false };
  }
  
  return { supabaseUrl, supabaseKey, isValid: true };
};

const { supabaseUrl, supabaseKey, isValid: envVarsValid } = loadEnvVars();

// Supabase 클라이언트 초기화 - 환경 변수가 유효하지 않으면 더미 클라이언트 반환
export const supabase = envVarsValid 
  ? createClient<Database>(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: isBrowser ? window.localStorage : undefined,
        storageKey: 'supabase.auth.token',
        flowType: 'pkce',
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    })
  : createClient<Database>('https://example.com', 'dummy-key'); // 더미 클라이언트

class SupabaseSync {
  private subscription: any = null;
  private initialized = false;
  private syncInterval: number | null = null;
  private retryCount = 0;
  private maxRetries = 5;
  private retryDelay = 1000;
  private reconnectTimeout: number | null = null;
  private offlineMode = false;
  private isDestroyed = false;
  private initTimeout: number | null = null;
  private isSyncing = false;
  private syncEnabled = true;
  private initializationPromise: Promise<boolean> | null = null;
  private reconnectInterval: number | null = null;
  
  async initialize(attempt: number = 1, maxAttempts: number = 3): Promise<boolean> {
    if (this.initialized || this.isDestroyed) {
      console.log('Already initialized or destroyed, skipping initialization');
      return this.initialized;
    }
    
    // 이미 초기화 중이면 기존 Promise 반환
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    console.log(`Initializing Supabase sync (attempt ${attempt}/${maxAttempts})...`);
    
    // 초기화 로직을 Promise로 래핑
    this.initializationPromise = new Promise<boolean>(async (resolve) => {
      // 전체 초기화 과정에 대한 타임아웃 설정
      this.clearInitTimeout();
      
      this.initTimeout = safeSetTimeout(() => {
        console.warn('Initialization timed out');
        
        // 타임아웃 발생 시 오프라인 모드로 전환 후 사용자에게 알림
        this.offlineMode = true;
        
        if (isBrowser) {
          toast.error('서버 연결 시간이 초과되었습니다. 오프라인 모드로 전환합니다.');
        }
        
        // 실패했지만 앱은 계속 진행할 수 있도록 함
        this.initialized = true;
        this.setupReconnectInterval();
        resolve(false);
      }, 15000);
      
      // 환경 변수가 유효하지 않으면 초기화 중단
      if (!envVarsValid) {
        this.clearInitTimeout();
        this.offlineMode = true;
        this.initialized = true; // 실패했지만 초기화는 완료된 것으로 처리
        resolve(false);
        return;
      }
      
      try {
        // 세션 확인
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }
        
        if (!session) {
          console.log('No active session, skipping initialization');
          this.clearInitTimeout();
          this.initialized = true; // 세션은 없지만 초기화는 완료
          resolve(true);
          return;
        }
        
        // 간단한 쿼리로 연결 테스트
        const { error: healthCheckError } = await supabase
          .from('skus')
          .select('count')
          .limit(1)
          .single();
        
        // "no rows" 에러는 무시 (실제 데이터가 없는 경우)
        if (healthCheckError && !healthCheckError.message.includes('Results contain 0 rows')) {
          throw healthCheckError;
        }
        
        // 초기화 성공
        this.offlineMode = false;
        await this.syncFromServer();
        this.setupRealtimeSubscription();
        this.setupPeriodicSync();
        
        this.initialized = true;
        this.retryCount = 0;
        console.log('Supabase sync initialized successfully');
        
        this.clearInitTimeout();
        this.initializationPromise = null;
        resolve(true);
      } catch (error: any) {
        console.error(`Initialization error (attempt ${attempt}/${maxAttempts}):`, error);
        this.clearInitTimeout();
        
        // 재시도 처리
        const shouldRetry = await this.handleInitializationFailure(attempt, maxAttempts, error);
        this.initializationPromise = null;
        resolve(shouldRetry);
      }
    });
    
    return this.initializationPromise;
  }
  
  private async handleInitializationFailure(
    attempt: number, 
    maxAttempts: number, 
    error?: any
  ): Promise<boolean> {
    if (attempt < maxAttempts) {
      console.log(`Retrying initialization in 2 seconds (${attempt}/${maxAttempts})...`);
      await new Promise(resolve => safeSetTimeout(resolve, 2000));
      return this.initialize(attempt + 1, maxAttempts);
    }
    
    // 최대 시도 횟수 초과 시 오프라인 모드로 전환
    this.offlineMode = true;
    this.initialized = true; // 실패했지만 초기화는 완료된 것으로 처리
    
    // JWT 또는 세션 관련 오류인 경우 - 로그인 화면으로 리디렉션
    if (error?.message?.includes('JWT') || error?.message?.includes('session')) {
      this.handleAuthError();
      return false;
    }
    
    const errorMessage = error instanceof Error ? error.message : '서버 연결에 실패했습니다';
    
    if (isBrowser) {
      toast.error(`${errorMessage}. 오프라인 모드로 전환합니다.`);
    }
    
    // 주기적으로 재연결 시도
    this.setupReconnectInterval();
    return false;
  }
  
  private handleAuthError() {
    this.destroy();
    
    if (isBrowser) {
      toast.error('세션이 만료되었습니다. 다시 로그인해주세요.');
      safeSetTimeout(() => window.location.reload(), 1000);
    }
  }
  
  private clearInitTimeout() {
    safeClearTimeout(this.initTimeout);
    this.initTimeout = null;
  }
  
  private setupReconnectInterval() {
    if (this.reconnectInterval) {
      safeClearInterval(this.reconnectInterval);
    }
    
    this.reconnectInterval = safeSetInterval(async () => {
      if (!this.offlineMode || this.isDestroyed) {
        safeClearInterval(this.reconnectInterval);
        this.reconnectInterval = null;
        return;
      }
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return; // 세션이 없으면 재연결 시도 중지
        
        console.log('Attempting to reconnect to server...');
        const isConnected = await this.initialize();
        
        if (isConnected) {
          this.offlineMode = false;
          safeClearInterval(this.reconnectInterval);
          this.reconnectInterval = null;
          
          if (isBrowser) {
            toast.success('서버에 다시 연결되었습니다');
          }
        }
      } catch (error) {
        console.error('Reconnection attempt failed:', error);
      }
    }, 30000); // 30초마다 재연결 시도
  }
  
  private setupRealtimeSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    
    if (!envVarsValid || !isBrowser) {
      console.log('Skipping realtime subscription setup in non-browser or invalid env vars');
      return;
    }
    
    try {
      this.subscription = supabase
        .channel('skus_changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'skus' },
          this.handleRealtimeChange.bind(this)
        )
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            console.log('Realtime subscription active');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Realtime subscription failed');
            this.handleConnectionError(new Error('Realtime connection failed'));
          }
        });
    } catch (error) {
      console.error('Failed to setup realtime subscription:', error);
      // 실시간 구독 실패는 치명적이지 않으므로 앱 동작은 계속 유지
    }
  }
  
  private setupPeriodicSync() {
    if (this.syncInterval) {
      safeClearInterval(this.syncInterval);
    }
    
    if (!envVarsValid || !isBrowser) {
      console.log('Skipping periodic sync setup in non-browser or invalid env vars');
      return;
    }
    
    this.syncInterval = safeSetInterval(async () => {
      if (!this.offlineMode && !this.isDestroyed && this.initialized && this.syncEnabled) {
        try {
          await this.syncFromServer();
        } catch (error) {
          console.error('Periodic sync failed:', error);
          // 주기적 동기화 실패는 치명적이지 않으므로 오류만 기록
        }
      }
    }, 30000); // 30초마다 동기화
  }
  
  private handleConnectionError(error: any) {
    if (this.isDestroyed) return;
    
    console.error('Connection error:', error);
    this.offlineMode = true;
    
    // JWT 또는 세션 관련 오류인 경우 - 로그인 화면으로 리디렉션
    if (error?.message?.includes('JWT') || error?.message?.includes('session')) {
      this.handleAuthError();
      return;
    }
    
    const errorMessage = error instanceof Error ? error.message : '서버 연결이 끊어졌습니다';
    
    if (isBrowser) {
      toast.error(`${errorMessage}. 재연결을 시도합니다.`);
    }
    
    this.retryConnection();
  }
  
  private async retryConnection() {
    if (this.isDestroyed || this.retryCount >= this.maxRetries) {
      if (this.retryCount >= this.maxRetries) {
        console.error('Max retry attempts exceeded');
        
        if (isBrowser) {
          toast.error('서버 연결을 복구할 수 없습니다. 페이지를 새로고침해주세요.');
        }
        
        this.setupReconnectInterval(); // 주기적으로 재연결 시도
      }
      return;
    }
    
    this.retryCount++;
    const delay = Math.min(this.retryDelay * Math.pow(2, this.retryCount - 1), 30000); // 최대 30초까지 지수 백오프
    
    if (this.reconnectTimeout) {
      safeClearTimeout(this.reconnectTimeout);
    }
    
    this.reconnectTimeout = safeSetTimeout(async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }
        
        if (!session) {
          throw new Error('세션이 만료되었습니다');
        }
        
        const isConnected = await this.initialize();
        
        if (isConnected) {
          this.offlineMode = false;
          this.retryCount = 0;
          
          if (isBrowser) {
            toast.success('서버에 다시 연결되었습니다');
          }
        }
      } catch (error) {
        console.error('Retry attempt failed:', error);
        await this.retryConnection(); // 재귀적으로 재시도하되, 최대 시도 횟수로 제한됨
      }
    }, delay);
  }
  
  private async handleRealtimeChange(payload: { new: any, old: any }) {
    if (this.offlineMode || this.isDestroyed || !this.initialized || !this.syncEnabled) return;
    
    try {
      const sku = payload.new;
      if (!sku?.id) return;
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Session error during realtime update:', sessionError);
        return;
      }
      
      await this.updateLocalSku({
        id: sku.id,
        styleNo: sku.style_no,
        color: sku.color,
        size: sku.size,
        expectedQuantity: sku.expected_quantity,
        actualQuantity: sku.actual_quantity,
        status: sku.status,
        progress: sku.progress,
        lastScanned: sku.last_scanned ? new Date(sku.last_scanned) : null,
        lastModified: new Date(sku.last_modified),
        userId: sku.user_id,
      });
    } catch (error) {
      console.error('Realtime update failed:', error);
    }
  }
  
  private async updateLocalSku(serverSku: SKU) {
    if (this.isDestroyed || !isBrowser) return;
    
    try {
      await db.skus.put(serverSku);
    } catch (error) {
      console.error(`Local SKU update failed (${serverSku.id}):`, error);
    }
  }
  
  enableSync() {
    this.syncEnabled = true;
    
    // 동기화가 활성화되면 바로 서버와 동기화 시도
    if (this.initialized && !this.offlineMode && !this.isDestroyed) {
      this.syncFromServer().catch(error => {
        console.error('Sync error after enabling:', error);
      });
    }
  }
  
  disableSync() {
    this.syncEnabled = false;
  }
  
  async syncToServer(sku: SKU) {
    if (!envVarsValid || !this.syncEnabled || this.offlineMode || this.isDestroyed || this.isSyncing || !this.initialized) {
      console.log('Skipping server sync:', { 
        envVarsValid,
        syncEnabled: this.syncEnabled,
        offlineMode: this.offlineMode,
        isDestroyed: this.isDestroyed,
        isSyncing: this.isSyncing,
        initialized: this.initialized
      });
      return;
    }
    
    this.isSyncing = true;
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      if (!session) {
        throw new Error('세션이 만료되었습니다');
      }
      
      const { error } = await supabase.from('skus').upsert({
        id: sku.id,
        style_no: sku.styleNo,
        color: sku.color,
        size: sku.size,
        expected_quantity: sku.expectedQuantity,
        actual_quantity: sku.actualQuantity,
        status: sku.status,
        progress: sku.progress,
        last_scanned: sku.lastScanned ? sku.lastScanned.toISOString() : null,
        last_modified: new Date().toISOString(),
        user_id: session.user.id,
      });
      
      if (error) throw error;
    } catch (error: any) {
      console.error('Server sync failed:', error.message);
      this.handleConnectionError(error);
    } finally {
      this.isSyncing = false;
    }
  }
  
  async syncFromServer() {
    if (!envVarsValid || this.offlineMode || this.isDestroyed || this.isSyncing || !this.initialized || !this.syncEnabled) {
      console.log('Skipping server sync:', {
        envVarsValid,
        offlineMode: this.offlineMode,
        isDestroyed: this.isDestroyed,
        isSyncing: this.isSyncing,
        initialized: this.initialized,
        syncEnabled: this.syncEnabled
      });
      return;
    }
    
    this.isSyncing = true;
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw sessionError;
      }
      
      if (!session) {
        throw new Error('세션이 만료되었습니다');
      }
      
      const { data: skus, error } = await supabase
        .from('skus')
        .select('*')
        .or(`user_id.eq.${session.user.id},user_id.is.null`);
      
      if (error) throw error;
      
      if (skus && skus.length > 0) {
        await db.transaction('rw', db.skus, async () => {
          await Promise.all(
            skus.map((sku) =>
              this.updateLocalSku({
                id: sku.id,
                styleNo: sku.style_no,
                color: sku.color,
                size: sku.size,
                expectedQuantity: sku.expected_quantity,
                actualQuantity: sku.actual_quantity,
                status: sku.status,
                progress: sku.progress,
                lastScanned: sku.last_scanned ? new Date(sku.last_scanned) : null,
                lastModified: new Date(sku.last_modified),
                userId: sku.user_id,
              })
            )
          );
        });
      }
    } catch (error: any) {
      console.error('Server sync failed:', error.message);
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }
  
  async getInitializationStatus(): Promise<{
    initialized: boolean;
    offlineMode: boolean;
    syncEnabled: boolean;
  }> {
    return {
      initialized: this.initialized,
      offlineMode: this.offlineMode,
      syncEnabled: this.syncEnabled
    };
  }
  
  destroy() {
    console.log('Cleaning up Supabase sync...');
    
    this.isDestroyed = true;
    this.initialized = false;
    this.offlineMode = false;
    this.isSyncing = false;
    this.syncEnabled = true;
    this.retryCount = 0;
    this.initializationPromise = null;
    
    safeClearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = null;
    
    safeClearInterval(this.reconnectInterval);
    this.reconnectInterval = null;
    
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    
    safeClearInterval(this.syncInterval);
    this.syncInterval = null;
    
    this.clearInitTimeout();
  }
}

export const supabaseSync = new SupabaseSync();

// 공용 연결 상태 확인 함수 - 앱 전체에서 사용 가능
export async function checkSupabaseConnection(): Promise<boolean> {
  if (!envVarsValid) {
    console.error('Supabase environment variables are not valid');
    return false;
  }
  
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError.message);
      return false;
    }
    
    // 세션이 없으면 로그인이 필요하지만, 연결 자체는 성공
    if (!session) {
      console.log('No active session, but connection successful');
      return true;
    }
    
    // 간단한 쿼리로 연결 테스트
    const { error: queryError } = await supabase
      .from('skus')
      .select('count')
      .limit(1)
      .single();
    
    // "no rows" 에러는 정상 (데이터가 없는 경우)
    const isQuerySuccessful = !queryError || queryError.message.includes('Results contain 0 rows');
    
    if (!isQuerySuccessful) {
      console.error('Database query error:', queryError.message);
      return false;
    }
    
    return true;
  } catch (error: any) {
    console.error('Connection check failed:', error.message);
    return false;
  }
}